import { ParseTree } from '../Parse/ParseTree';
import { Expr } from './Expr';
import { ExprPath } from './ExprPath';
import { ExprValue } from './ExprValue';
export var ExprComp;
(function (ExprComp) {
    let Type;
    (function (Type) {
        Type["Success"] = "SUCCESS";
        Type["Failure"] = "FAILURE";
    })(Type = ExprComp.Type || (ExprComp.Type = {}));
    ExprComp.CapturePattern = /^\$([\d]+)$/;
    function success(exprs) {
        return { exprs, type: "SUCCESS" };
    }
    function failure(exprs) {
        return { exprs, type: "FAILURE" };
    }
    function capture(canonicalRoot, candidateRoot) {
        const patternNodes = ParseTree.flatten(canonicalRoot);
        if (patternNodes instanceof Error) {
            return patternNodes;
        }
        const candidateNodes = ParseTree.flatten(candidateRoot);
        if (candidateNodes instanceof Error) {
            return candidateNodes;
        }
        let position = 0;
        let groups = [];
        const captures = [];
        for (const patternNode of patternNodes) {
            if (position >= candidateNodes.length) {
                return failure(captures);
            }
            const group = getMatchGroup(patternNode);
            if (group !== undefined) {
                groups.unshift(group);
                continue;
            }
            if (groups.length === 0) {
                if (match(patternNode, candidateNodes[position])) {
                    position += 1;
                    continue;
                }
                return failure(captures);
            }
            let matchPos = seek(patternNode, candidateNodes, position);
            position = matchPos;
            if (matchPos === -1) {
                position = candidateNodes.length;
                break;
            }
            for (const { canonicalNode, group } of groups) {
                const matchNode = candidateNodes[matchPos - 1];
                if (matchNode == null) {
                    return failure(captures);
                }
                const expr = Expr.Parsed.make(matchNode);
                if (expr instanceof Error) {
                    return expr;
                }
                const matchNodes = getNodesInGroup(canonicalNode, matchNode);
                if (matchNodes instanceof Error) {
                    return matchNodes;
                }
                captures[group] = expr;
                matchPos -= matchNodes.length;
            }
            groups = [];
            position += 1;
        }
        if (groups.length > 0) {
            if (position === candidateNodes.length) {
                return failure(captures);
            }
            let matchPos = candidateNodes.length;
            for (const { canonicalNode, group } of groups) {
                const matchNode = candidateNodes[matchPos - 1];
                if (matchNode == null) {
                    return failure(captures);
                }
                const expr = Expr.Parsed.make(matchNode);
                if (expr instanceof Error) {
                    return expr;
                }
                const matchNodes = getNodesInGroup(canonicalNode, matchNode);
                if (matchNodes instanceof Error) {
                    return matchNodes;
                }
                captures[group] = expr;
                matchPos -= matchNodes.length;
            }
            return success(captures);
        }
        if (position < candidateNodes.length) {
            return failure(captures);
        }
        return success(captures);
    }
    ExprComp.capture = capture;
    function getMatchGroup(canonicalNode) {
        if (canonicalNode.type === "Identifier" ||
            canonicalNode.type === "CallExpression") {
            const matched = ExprComp.CapturePattern.exec(canonicalNode.type === "CallExpression"
                ? canonicalNode.callee
                : canonicalNode.name);
            if (matched != null) {
                const group = parseInt(matched[1], 10);
                return {
                    canonicalNode,
                    group,
                };
            }
        }
    }
    function getNodesInGroup(canonicalNode, candidateNode) {
        if (canonicalNode.type === "CallExpression" &&
            candidateNode.type === "CallExpression" &&
            ExprComp.CapturePattern.test(canonicalNode.callee)) {
            return [candidateNode];
        }
        return ParseTree.flatten(candidateNode);
    }
    function seek(patterNode, candidateNodes, position) {
        while (position < candidateNodes.length) {
            const candidateNode = candidateNodes[position];
            if (match(patterNode, candidateNode)) {
                return position;
            }
            position += 1;
        }
        return -1;
    }
    function match(canonicalNode, candidateNode) {
        switch (canonicalNode.type) {
            case "NullLiteral": {
                return true;
            }
            case "BooleanLiteral": {
                if (candidateNode.type === "BooleanLiteral") {
                    return canonicalNode.value === candidateNode.value;
                }
                break;
            }
            case "HexLiteral": {
                if (candidateNode.type === "HexLiteral") {
                    return canonicalNode.value === candidateNode.value;
                }
                break;
            }
            case "NumberLiteral": {
                if (candidateNode.type === "NumberLiteral") {
                    if (canonicalNode.value == null) {
                        return false;
                    }
                    return ExprValue.asDecimal(canonicalNode.value).eq(candidateNode.value);
                }
                break;
            }
            case "StringLiteral": {
                if (candidateNode.type === "StringLiteral") {
                    return canonicalNode.value === candidateNode.value;
                }
                break;
            }
            case "DateLiteral":
                if (candidateNode.type === "DateLiteral") {
                    return canonicalNode.value === candidateNode.value;
                }
                break;
            case "RegExpLiteral": {
                if (candidateNode.type === "RegExpLiteral") {
                    return canonicalNode.value === candidateNode.value;
                }
                break;
            }
            case "Identifier": {
                if (ExprComp.CapturePattern.test(canonicalNode.name)) {
                    return true;
                }
                else if (candidateNode.type === "Identifier") {
                    return canonicalNode.name === candidateNode.name;
                }
                break;
            }
            case "AccessIdentifier": {
                if (candidateNode.type === "AccessIdentifier") {
                    return canonicalNode.name === candidateNode.name;
                }
                break;
            }
            case "AccessExpression": {
                if (candidateNode.type === "AccessExpression") {
                    return matchAccess(canonicalNode, candidateNode);
                }
                break;
            }
            case "CallExpression": {
                if (candidateNode.type === "CallExpression") {
                    return (canonicalNode.callee === candidateNode.callee ||
                        ExprComp.CapturePattern.test(canonicalNode.callee));
                }
                break;
            }
            case "UnaryExpression": {
                if (candidateNode.type === "UnaryExpression") {
                    return canonicalNode.operator === candidateNode.operator;
                }
                break;
            }
            case "BinaryExpression": {
                if (candidateNode.type === "BinaryExpression") {
                    return canonicalNode.operator === candidateNode.operator;
                }
                break;
            }
            case "LogicalExpression": {
                if (candidateNode.type === "LogicalExpression") {
                    return canonicalNode.operator === candidateNode.operator;
                }
                break;
            }
            case "CaseExpression": {
                if (candidateNode.type === "CaseExpression") {
                    return true;
                }
                break;
            }
            case "CaseWhen": {
                if (candidateNode.type === "CaseWhen") {
                    return true;
                }
                break;
            }
            case "CaseElse": {
                if (candidateNode.type === "CaseElse") {
                    return true;
                }
                break;
            }
        }
        return false;
    }
    function matchAccess(canonicalNode, candidateNode) {
        const canonicalPath = ExprPath.makeAccessPath(canonicalNode);
        const candidatePath = ExprPath.makeAccessPath(candidateNode);
        if (canonicalPath.length !== candidatePath.length) {
            return false;
        }
        for (let i = 0; i < canonicalPath.length; ++i) {
            if (canonicalPath[i] !== candidatePath[i]) {
                return false;
            }
        }
        return true;
    }
})(ExprComp || (ExprComp = {}));
